/**
 * Bounded offline write queue (CONTRACTS.md §3, spec §11).
 *
 * - Persisted in localStorage under 'rust-sync-queue'.
 * - Hard bound of MAX_QUEUE_ENTRIES (100): when full, enqueue sets the
 *   overflow error state and throws a typed AppError — entries are NEVER
 *   silently dropped to make room.
 * - FIFO order is preserved during flush (syncQueueFlush order).
 * - Duplicate/debounce protection: an entry with an existing dedupeKey
 *   replaces that entry's payload instead of adding a second write.
 *
 * The queue is pure storage + bookkeeping; SyncProvider owns connectivity
 * and the flush loop. Serializable payloads only — never functions.
 */
import { makeAppError, newCorrelationId } from '@/lib/supabase/errors'
import type { AppError } from '@/types'

export const SYNC_QUEUE_KEY = 'rust-sync-queue'
export const MAX_QUEUE_ENTRIES = 100
export const MAX_QUEUE_ATTEMPTS = 8

export type QueuedWrite = {
  id: string
  /** Registry key resolved via registerSyncHandler (e.g. 'places.save'). */
  kind: string
  /** JSON-serializable payload for the handler. */
  payload: unknown
  /** Optional debounce identity; re-enqueueing the same key replaces payload. */
  dedupeKey: string | null
  attempts: number
  createdAt: string
  lastError: string | null
}

export type SyncQueueState = {
  version: 1
  /** Sticky error flag set when an enqueue hit the bound. */
  overflow: boolean
  entries: QueuedWrite[]
}

/** Always returns a fresh state with a fresh entries array (never shared). */
function emptyState(): SyncQueueState {
  return { version: 1, overflow: false, entries: [] }
}

function storageAvailable(): boolean {
  return typeof localStorage !== 'undefined'
}

export function loadQueue(): SyncQueueState {
  if (!storageAvailable()) return emptyState()
  try {
    const raw = localStorage.getItem(SYNC_QUEUE_KEY)
    if (!raw) return emptyState()
    const parsed = JSON.parse(raw) as Partial<SyncQueueState>
    if (!Array.isArray(parsed.entries)) return emptyState()
    return {
      version: 1,
      overflow: parsed.overflow === true,
      entries: parsed.entries.filter(
        (entry): entry is QueuedWrite =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as QueuedWrite).id === 'string' &&
          typeof (entry as QueuedWrite).kind === 'string',
      ),
    }
  } catch {
    return emptyState()
  }
}

export function saveQueue(state: SyncQueueState): void {
  if (!storageAvailable()) return
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(state))
    // Same-tab notification (the 'storage' event only fires across tabs).
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('rust-sync-queue-changed'))
    }
  } catch {
    // Quota errors surface as the queue being unwritable; the caller's write
    // to Supabase already happened or failed honestly elsewhere.
  }
}

export function queueOverflowError(): AppError {
  return makeAppError({
    code: 'SYNC_QUEUE_FULL',
    userMessage:
      'Er staan te veel wijzigingen te wachten op synchronisatie. Maak verbinding met internet om te synchroniseren.',
    technicalMessage: `offline write queue reached ${MAX_QUEUE_ENTRIES} entries`,
    retryable: false,
    source: 'browser',
    correlationId: newCorrelationId(),
  })
}

function newEntryId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `q_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Adds a write to the queue. Throws SYNC_QUEUE_FULL when the bound is hit
 * (no silent drop). Re-enqueueing an existing dedupeKey updates in place.
 */
export function enqueueWrite(input: {
  kind: string
  payload: unknown
  dedupeKey?: string
}): QueuedWrite {
  const state = loadQueue()
  const dedupeKey = input.dedupeKey ?? null

  if (dedupeKey) {
    const existing = state.entries.find((entry) => entry.dedupeKey === dedupeKey)
    if (existing) {
      existing.payload = input.payload
      existing.lastError = null
      saveQueue(state)
      return existing
    }
  }

  if (state.entries.length >= MAX_QUEUE_ENTRIES) {
    state.overflow = true
    saveQueue(state)
    throw queueOverflowError()
  }

  const entry: QueuedWrite = {
    id: newEntryId(),
    kind: input.kind,
    payload: input.payload,
    dedupeKey,
    attempts: 0,
    createdAt: new Date().toISOString(),
    lastError: null,
  }
  state.entries.push(entry)
  saveQueue(state)
  return entry
}

export function removeWrite(entryId: string): void {
  const state = loadQueue()
  const next = state.entries.filter((entry) => entry.id !== entryId)
  if (next.length !== state.entries.length) {
    saveQueue({ ...state, entries: next })
  }
}

export function markWriteAttempt(entryId: string, errorMessage: string | null): void {
  const state = loadQueue()
  const entry = state.entries.find((candidate) => candidate.id === entryId)
  if (!entry) return
  entry.attempts += 1
  entry.lastError = errorMessage
  saveQueue(state)
}

export function pendingWriteCount(): number {
  return loadQueue().entries.length
}

export function hasQueueOverflow(): boolean {
  return loadQueue().overflow
}

/** Clears the sticky overflow flag once the queue drained below the bound. */
export function clearQueueOverflow(): void {
  const state = loadQueue()
  if (state.overflow && state.entries.length < MAX_QUEUE_ENTRIES) {
    saveQueue({ ...state, overflow: false })
  }
}
